import {selectCurrentPlayer, selectUnits} from "../../../redux/reducers/sharedStateReducer/selectors";
import {playUnitRecruitmentEffects} from "../../../scenes/playUnitRecruitmentEffects";
import {
  addPendingRecruitmentAction,
  recruitUnit,
  selectUnit,
  setSelectedToken,
  showToast
} from "../../../redux/actions";
import {getUnitPrice} from "../../../configs/units";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {checkSameTypeUnitRecruitmentLimit, getSameTypeUnitLimit} from "./checkSameTypeUnitRecruitmentLimit";
import {createRandomId} from "../../ids/createRandomId";
import {ID_PREFIX_FOR_UNIT} from "../../../constants/ids";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {getActiveTutorialContext} from "../../../configs/tutorial";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {checkIfPlayerUnitsExceedSupply} from "./calcPlayerUnitsAndSupply";
import {checkIfPlayerUnitsNeverStarve} from "../supply/checkIfPlayerUnitsNeverStarve";

// Пауза перед подсказкой о снабжении: сначала должен отыграть звук найма
const SUPPLY_HINT_DELAY_MS = 450;

// Подсказка «отрядов больше, чем снабжения»: найм еще можно отменить, поэтому
// висит подольше обычного тоста
const UNITS_EXCEED_SUPPLY_TOAST_MS = 5000;
const UNITS_EXCEED_SUPPLY_TOAST_TEXT = 'Теперь у тебя больше отрядов, чем снабжения'
  + ' — кто-то будет голодать в следующем раунде.';

/**
 * Найм нового отряда на казарму: проверки, диспатчи и звук.
 *
 * Общий код для ОБОИХ порядков действий на этапе найма — «сначала тип отряда,
 * потом казарма» (клик по полю, см. GameScene) и «сначала казарма, потом тип»
 * (кнопка в футере, см. WarRecruitmentStageToolbar). Проверки и сообщения об
 * ошибках обязаны совпадать, поэтому живут в одном месте.
 *
 * Предполагается, что казарма уже проверена на пригодность (своя, свободная,
 * не заблокирована противником) — это делает calculateAvailableRecruitmentCells.
 *
 * @returns {boolean} нанят ли отряд
 */
export const recruitNewUnitOnBarracks = (store, soundManager, barracksHexKey, unitTypeId) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const units = selectUnits(state);

  if (checkIfFinalRound(state)) {
    store.dispatch(showToast('В последнем раунде нельзя нанимать новые отряды!', 'warning', 3000));
    return false;
  }

  const {price: unitPrice} = getUnitPrice(unitTypeId, currentPlayer);

  if (currentPlayer.coins < unitPrice) {
    store.dispatch(showToast(`Недостаточно монет для найма выбранного отряда!`, 'warning', 3000));
    return false;
  }

  const playerUnits = getPlayerUnits(currentPlayer, units);
  if (!checkSameTypeUnitRecruitmentLimit(currentPlayer, unitTypeId, playerUnits)) {
    const limit = getSameTypeUnitLimit(currentPlayer, unitTypeId);
    store.dispatch(showToast(`Нельзя иметь более ${limit} отрядов этого типа!`, 'warning', 5000));
    return false;
  }

  const newUnitId = createRandomId(ID_PREFIX_FOR_UNIT);
  store.dispatch(recruitUnit(
    barracksHexKey,
    unitTypeId,
    currentPlayer.factionId,
    unitPrice,
    newUnitId,
  ));

  // Отряд ищем в ОБНОВЛЕННОМ состоянии: идентификатор созданного отряда
  // назначает редьюсер, и он нужен и для отмены действия, и для выделения
  const recruitedUnit = selectUnits(store.getState()).find(unit => unit.hexKey === barracksHexKey);
  if (!recruitedUnit) {
    // Найм не применился (например, заблокирован гейтом обучения) — выходим
    // без истории/звука, объясняющий тост уже показан блокировщиком
    return false;
  }
  store.dispatch(addPendingRecruitmentAction(recruitedUnit.id, 'UNIT_RECRUITMENT', barracksHexKey, unitPrice, 0));

  // Тип отряда в тулбаре после найма сбрасывается — следующий найм начинается
  // с чистого выбора
  store.dispatch(setSelectedToken(null));

  // В обучении шаг после найма может требовать РУЧНОГО выбора отряда
  // (deselectsUnitOnEntry) — тогда автовыбор нанятого отряда пропускается:
  // найм уже продвинул сценарий, и активный шаг здесь — следующий
  const activeTutorialStep = getActiveTutorialContext(store.getState()).step;
  if (!activeTutorialStep?.deselectsUnitOnEntry) {
    store.dispatch(selectUnit(recruitedUnit.id));
  }

  // Отряд проявляется на ячейке из света, и только в этот момент звучит найм,
  // расходится волна цветом игрока и всплывает число потраченных монет
  playUnitRecruitmentEffects(store, REGISTRY_SINGLETON.getHexGridRenderer?.(), {
    unitId: recruitedUnit.id,
    hexKey: barracksHexKey,
    price: unitPrice,
    playRecruitmentSound: () => soundManager.playUnitRecruitment(
      recruitedUnit.type, recruitedUnit.ownerId, currentPlayer, unitPrice, recruitedUnit
    ),
    playCastSound: () => soundManager.playUnitRecruitmentCast?.(recruitedUnit.type),
    playImpactSound: () => soundManager.playUnitRecruitmentImpact?.(recruitedUnit.type),
  });

  // Нанятый отряд может оказаться там, куда снабжение не дотягивается, — тогда
  // показываем ту же подсказку с путем от ближайшей фермы, что и после
  // перемещения отряда за пределы снабжения. Сцена берется из реестра: найм
  // приходит из двух мест (клик по полю и кнопка в футере), а обращение к полю
  // нужно обоим — как и звук выше
  REGISTRY_SINGLETON.getGameScene()?.showSupplyOutOfRangeHintIfNeeded(
    barracksHexKey,
    store.getState(),
    {delayMs: SUPPLY_HINT_DELAY_MS},
  );

  // Нанятому отряду может не хватить пайка: сравниваем те же два числа, что
  // показывает плашка этапа найма (RecruitmentSummary). Считаем по состоянию
  // ПОСЛЕ найма — отряд в нем уже есть.
  //
  // Инсектоидов это не касается — их отряды не голодают вовсе
  // (см. checkIfPlayerUnitsNeverStarve, там же и почему)
  const canUnitsStarve = !checkIfPlayerUnitsNeverStarve(currentPlayer);
  if (canUnitsStarve && checkIfPlayerUnitsExceedSupply(store.getState(), currentPlayer)) {
    // Со звуком: предупреждение должно быть слышно поверх звука найма
    store.dispatch(showToast(
      UNITS_EXCEED_SUPPLY_TOAST_TEXT,
      'warning',
      UNITS_EXCEED_SUPPLY_TOAST_MS
    ));
  }

  return true;
};
